import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import accountsApi from "../../services/accountsCommonService";
import AccountsConstants from "../../common/AccountsConstants";
import { formatDate, formatINR, todayInput } from "./accountUser";
import { EntryPreview } from "./VoucherEntry";
import { ApiAlert, CreditPanel } from "./AccountWidgets";
import PageShell from "./PageShell";
import "./accounts.css";

export function OutstandingPage() {
  const navigate = useNavigate();
  const [asOfDate, setAsOfDate] = useState(todayInput());
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async (date = asOfDate) => {
    setLoading(true);
    setError(null);
    try {
      const result = await accountsApi.outstandingReport({ asOfDate: date });
      setReport(result.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(asOfDate); }, []);
  const totals = report?.totals || {};
  return (
    <PageShell title="Outstanding" subtitle={report ? `As of ${formatDate(report.asOfDate)}` : ""}>
      <ApiAlert error={error} />
      {report && (
        <div className="accounts-summary">
          <div><span>Total outstanding</span><strong>{formatINR(totals.outstanding)}</strong></div>
          <div><span>Total overdue</span><strong>{formatINR(totals.amountDue)}</strong></div>
          <div><span>Over limit</span><strong>{(report.rows || []).filter((row) => row.overLimit).length}</strong></div>
          <div><span>Open orders</span><strong>{formatINR(totals.openOrders)}</strong></div>
        </div>
      )}
      <form className="accounts-form" onSubmit={(event) => { event.preventDefault(); load(asOfDate); }}>
        <div className="accounts-filter-bar accounts-filter-compact">
          <label className="tally-field">As-of date
            <input className="form-control" type="date" value={asOfDate} onChange={(event) => setAsOfDate(event.target.value)} />
          </label>
          <div className="accounts-toolbar">
            <button className="btn btn-primary" type="submit">Show</button>
          </div>
        </div>
      </form>
      <div className={`accounts-table-wrap ${loading ? "accounts-dim" : ""}`}>
        <table className="accounts-table">
          <thead>
            <tr><th>Customer</th><th className="num">Credit limit</th><th className="num">Credit days</th><th className="num">Outstanding</th><th className="num">Amount due</th><th className="num">Not yet due</th><th className="num">Open orders</th><th className="num">Available credit</th></tr>
          </thead>
          <tbody>
            {(report?.rows || []).map((row) => (
              <tr key={row.customerId}>
                <td>
                  <button type="button" className="accounts-voucher-link" onClick={() => navigate(AccountsConstants.customerOutstanding(row.customerId))}>{row.customerName}</button>
                  {row.overLimit && <span className="accounts-chip warn">Over limit</span>}
                </td>
                <td className="num">{formatINR(row.creditLimit)}</td>
                <td className="num">{row.creditDays}</td>
                <td className="num"><strong>{formatINR(row.outstanding)}</strong></td>
                <td className={`num ${row.amountDue > 0 ? "accounts-money-neg" : ""}`}>{formatINR(row.amountDue)}</td>
                <td className="num">{formatINR(row.notYetDue)}</td>
                <td className="num">{formatINR(row.openOrders)}</td>
                <td className={`num ${row.availableCredit < 0 ? "accounts-money-neg" : ""}`}>{formatINR(row.availableCredit)}</td>
              </tr>
            ))}
            {(report?.rows || []).length === 0 && <tr><td colSpan={8}>No customers.</td></tr>}
          </tbody>
          {report && (
            <tfoot>
              <tr>
                <td colSpan={3}>Total</td>
                <td className="num">{formatINR(totals.outstanding)}</td>
                <td className="num">{formatINR(totals.amountDue)}</td>
                <td className="num">{formatINR(totals.notYetDue)}</td>
                <td className="num">{formatINR(totals.openOrders)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </PageShell>
  );
}

export function CustomerOutstandingPage() {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const load = () => {
    accountsApi.customerOutstanding(customerId)
      .then((result) => setReport(result.data))
      .catch((err) => setError(err));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [customerId]);
  if (!report) return <PageShell title="Customer outstanding"><ApiAlert error={error} />{!error && <p>Loading…</p>}</PageShell>;
  return (
    <PageShell title={report.customer.name} subtitle="Receipts are applied to the oldest bill first.">
      <div className="accounts-form-actions start">
        <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(AccountsConstants.OUTSTANDING)}>All customers</button>
        <button type="button" className="btn btn-outline-primary" onClick={() => navigate(`${AccountsConstants.LEDGER_STATEMENT}?ledgerId=${report.customer.ledgerId}`)}>Ledger statement</button>
        <button type="button" className="btn btn-primary" onClick={() => navigate(AccountsConstants.RECEIPT_CREATE)}>Record receipt</button>
      </div>
      <div className="accounts-entry">
        <div className="accounts-panel">
          <CreditPanel customerId={customerId} />
          {Number(report.advance) > 0 && <p>Advance received <strong>{formatINR(report.advance)}</strong></p>}
        </div>
        <div className="accounts-table-wrap">
          <table className="accounts-table">
            <thead><tr><th>Bill date</th><th>Bill</th><th>Due date</th><th className="num">Bill amount</th><th className="num">Received</th><th className="num">Open amount</th><th>Days overdue</th></tr></thead>
            <tbody>
              {(report.bills || []).map((bill) => (
                <tr key={bill.voucherNo}>
                  <td>{formatDate(bill.date)}</td>
                  <td>{bill.voucherId ? <button type="button" className="accounts-voucher-link" onClick={() => navigate(`${AccountsConstants.VOUCHER_VIEW}?id=${bill.voucherId}`)}>{bill.voucherNo}</button> : bill.voucherNo}</td>
                  <td>{formatDate(bill.dueDate)}</td>
                  <td className="num">{formatINR(bill.amount)}</td>
                  <td className="num">{formatINR(bill.paid)}</td>
                  <td className="num">{formatINR(bill.open)}</td>
                  <td>{bill.overdue ? (bill.daysOverdue === null || bill.daysOverdue === undefined ? "Due" : <span className="accounts-money-neg">{bill.daysOverdue}</span>) : "Not due"}</td>
                </tr>
              ))}
              {(report.bills || []).length === 0 && <tr><td colSpan={7}>No open bills.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </PageShell>
  );
}

export function AgingPage() {
  const [asOfDate, setAsOfDate] = useState(todayInput());
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const load = async (date = asOfDate) => {
    setError(null);
    try {
      const result = await accountsApi.agingReport({ asOfDate: date });
      setReport(result.data);
    } catch (err) {
      setError(err);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(asOfDate); }, []);
  const buckets = report?.buckets || [];
  const totals = report?.totals || {};
  return (
    <PageShell title="Aging" subtitle="An opening balance has no bill date, so it is shown in the oldest column.">
      <ApiAlert error={error} />
      <form className="accounts-form" onSubmit={(event) => { event.preventDefault(); load(asOfDate); }}>
        <div className="accounts-filter-bar accounts-filter-compact">
          <label className="tally-field">As-of date
            <input className="form-control" type="date" value={asOfDate} onChange={(event) => setAsOfDate(event.target.value)} />
          </label>
          <div className="accounts-toolbar">
            <button className="btn btn-primary" type="submit">Show</button>
          </div>
        </div>
      </form>
      <div className="accounts-table-wrap">
        <table className="accounts-table">
          <thead>
            <tr>
              <th>Customer</th>
              {buckets.map((bucket) => <th key={bucket.key} className="num">{bucket.label}</th>)}
              <th className="num">Advance</th>
              <th className="num">Outstanding</th>
            </tr>
          </thead>
          <tbody>
            {(report?.rows || []).map((row) => (
              <tr key={row.customerId}>
                <td>{row.customerName}</td>
                {buckets.map((bucket, index) => (
                  <td key={bucket.key} className={`num ${index === buckets.length - 1 && row[bucket.key] > 0 ? "accounts-money-neg" : ""}`}>{formatINR(row[bucket.key])}</td>
                ))}
                <td className="num">{formatINR(row.advance)}</td>
                <td className="num">{formatINR(row.outstanding)}</td>
              </tr>
            ))}
            {(report?.rows || []).length === 0 && <tr><td colSpan={2 + buckets.length}>No outstanding balances.</td></tr>}
          </tbody>
          {report && (
            <tfoot>
              <tr>
                <td>Total</td>
                {buckets.map((bucket) => <td key={bucket.key} className="num">{formatINR(totals[bucket.key])}</td>)}
                <td className="num">{formatINR(totals.advance)}</td>
                <td className="num">{formatINR(totals.outstanding)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </PageShell>
  );
}

export function PayablesPage() {
  const navigate = useNavigate();
  const [asOfDate, setAsOfDate] = useState(todayInput());
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const load = async (date = asOfDate) => {
    setError(null);
    try {
      const result = await accountsApi.payablesReport({ asOfDate: date });
      setReport(result.data);
    } catch (err) {
      setError(err);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(asOfDate); }, []);
  const totals = report?.totals || {};
  return (
    <PageShell title="Payables" actions={<button type="button" className="btn btn-primary" onClick={() => navigate(AccountsConstants.PAYMENT_CREATE)}>Record payment</button>}>
      <ApiAlert error={error} />
      <form className="accounts-form" onSubmit={(event) => { event.preventDefault(); load(asOfDate); }}>
        <div className="accounts-filter-bar accounts-filter-compact">
          <label className="tally-field">As-of date
            <input className="form-control" type="date" value={asOfDate} onChange={(event) => setAsOfDate(event.target.value)} />
          </label>
          <div className="accounts-toolbar">
            <button className="btn btn-primary" type="submit">Show</button>
          </div>
        </div>
      </form>
      <div className="accounts-table-wrap">
        <table className="accounts-table">
          <thead><tr><th>Supplier</th><th className="num">Payable</th><th className="num">Advance paid</th></tr></thead>
          <tbody>
            {(report?.rows || []).map((row) => (
              <tr key={row.ledgerId}><td>{row.supplierName}</td><td className="num">{formatINR(row.payable)}</td><td className="num">{formatINR(row.advancePaid)}</td></tr>
            ))}
            {(report?.rows || []).length === 0 && <tr><td colSpan={3}>No supplier balances.</td></tr>}
          </tbody>
          {report && <tfoot><tr><td>Total</td><td className="num">{formatINR(totals.payable)}</td><td className="num">{formatINR(totals.advancePaid)}</td></tr></tfoot>}
        </table>
      </div>
    </PageShell>
  );
}

export function DayBookPage() {
  const navigate = useNavigate();
  const [date, setDate] = useState(todayInput());
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const load = async (next = date) => {
    setError(null);
    try {
      const result = await accountsApi.dayBook({ date: next });
      setReport(result.data);
    } catch (err) {
      setError(err);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(date); }, []);
  const vouchers = report?.vouchers || [];
  return (
    <PageShell title="Day Book" subtitle={report ? `${report.totals.count} vouchers · Total ${formatINR(report.totals.debit)}` : ""}>
      <ApiAlert error={error} />
      <form className="accounts-form" onSubmit={(event) => { event.preventDefault(); load(date); }}>
        <div className="accounts-filter-bar accounts-filter-compact">
          <label className="tally-field">Date
            <input className="form-control" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          </label>
          <div className="accounts-toolbar">
            <button className="btn btn-primary" type="submit">Show</button>
          </div>
        </div>
      </form>
      {vouchers.length === 0 && <p>No posted vouchers on {formatDate(date)}.</p>}
      {vouchers.map((voucher) => (
        <div className="accounts-panel" key={voucher.id}>
          <button type="button" className="accounts-voucher-link" onClick={() => navigate(`${AccountsConstants.VOUCHER_VIEW}?id=${voucher.id}`)}>{voucher.voucherNo}</button>
          <span> {voucher.voucherType}</span>
          <p className="accounts-hint">{[voucher.paymentMode || voucher.mode, voucher.referenceNo].filter(Boolean).join(" · ")}</p>
          <EntryPreview lines={voucher.lines || []} />
          {voucher.narration && <p className="accounts-narration">{voucher.narration}</p>}
        </div>
      ))}
    </PageShell>
  );
}
